import React, { useState, useEffect, useRef } from 'react';
import { Select, Toast, Button, Avatar } from '@douyinfe/semi-ui';
import { IconRefresh, IconUser } from '@douyinfe/semi-icons';
import request from '../../utils/request';

const UserSelector = ({ value, onChange, placeholder, disabled, multiple = true }) => {
    const [loading, setLoading] = useState(false);
    const [userList, setUserList] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, limit: 10, total: 0 });
    const [searchKey, setSearchKey] = useState('');
    const [internalValue, setInternalValue] = useState([]);
    const searchTimeoutRef = useRef(null);
    // 用于补全单个uid的详情
    const requestedUidsRef = useRef(new Set());
    const [notFoundUids, setNotFoundUids] = useState([]);

    // 新增：判断 value 里的 id 是否都在 userList 里
    const valueArray = Array.isArray(value) ? value : value ? [value] : [];
    const validValues = valueArray.filter(v => v && v !== '0');
    const derivedLoading = validValues.length > 0 && validValues.some(uid => !userList.some(user => user.uid === uid) && !notFoundUids.includes(uid));

    const fetchUserList = async (selectedUids = []) => {
        // 返回Promise以便后续操作
        setLoading(true);
        try {
            // 首先获取已选中用户的详细信息
            let selectedUsers = [];
            if (Array.isArray(selectedUids) && selectedUids.length > 0) {
                const selectedResponse = await request.get('/go/sp/su/listmini_service_user/', {
                    params: {
                        uids: selectedUids.join(',')
                    }
                });
                if (selectedResponse.errno === 0 && selectedResponse.list) {
                    selectedUsers = selectedResponse.list;
                }
            }

            // 获取搜索结果
            const response = await request.get('/go/sp/su/listmini_service_user/', {
                params: {
                    page: pagination.page,
                    limit: pagination.limit,
                    key: searchKey,
                    status: -1
                }
            });

            if (response.errno === 0) {
                // 合并搜索结果和已选中用户
                const searchResults = response.list || [];
                const mergedList = [...searchResults];

                // 将不在搜索结果中的已选中用户添加到列表中
                selectedUsers.forEach(selectedUser => {
                    if (!mergedList.some(user => user.uid === selectedUser.uid)) {
                        mergedList.push(selectedUser);
                    }
                });

                setUserList(mergedList);
                setPagination(prev => ({
                    ...prev,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取用户列表失败');
            }
        } catch (error) {
            console.error('获取用户列表失败:', error);
            Toast.error('获取用户列表失败');
        } finally {
            setLoading(false);
        }
        return Promise.resolve(); // 返回Promise以便链式调用
    };

    useEffect(() => {
        if (value) {
            const valueArray = Array.isArray(value) ? value : [value];
            const validValues = valueArray.filter(v => v && v !== '0');
            setInternalValue(validValues);

            // 检查 userList 里是否有所有 value 对应的用户，没有就拉取
            const missingUsers = validValues.filter(uid => !userList.some(user => user.uid === uid));
            if (missingUsers.length > 0) {
                fetchUserDetails(missingUsers);
            }
        } else {
            setInternalValue([]);
        }
    }, [value, userList]); // 依赖 userList，确保拉取后能刷新

    const fetchUserDetails = async (uids) => {
        try {
            const response = await request.get('/go/sp/su/listmini_service_user/', {
                params: {
                    uids: uids.join(',')
                }
            });
            if (response.errno === 0 && response.list) {
                setUserList(prevList => {
                    const newList = [...prevList];
                    response.list.forEach(user => {
                        // 查找是否已存在该用户
                        const existingUserIndex = newList.findIndex(existingUser => existingUser.uid === user.uid);
                        if (existingUserIndex === -1) {
                            // 如果不存在，添加新用户
                            newList.push(user);
                        } else {
                            // 如果存在，更新用户信息
                            newList[existingUserIndex] = user;
                        }
                    });
                    return newList;
                });
                // 标记查不到的id
                const foundUids = response.list.map(u => u.uid);
                const notFound = uids.filter(uid => !foundUids.includes(uid));
                if (notFound.length > 0) setNotFoundUids(prev => Array.from(new Set([...prev, ...notFound])));
            } else {
                // 全部查不到
                setNotFoundUids(prev => Array.from(new Set([...prev, ...uids])));
            }
        } catch (error) {
            setNotFoundUids(prev => Array.from(new Set([...prev, ...uids])));
            console.error('获取用户详情失败:', error);
        }
    };

    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            // 搜索前保存当前已选中的用户ID
            fetchUserList(internalValue);
        }, 300);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchKey, pagination.page]); // 移除 internalValue 依赖，防止无限循环

    const handleChange = (selectedUids) => {
        setInternalValue(selectedUids);
        onChange?.(selectedUids);
    };

    const renderCustomOption = (user) => {
        const optionStyle = {
            display: 'flex',
            paddingLeft: 24,
            paddingTop: 10,
            paddingBottom: 10,
        };
        return (
            <Select.Option
                key={user.uid}
                value={user.uid}
                label={user.name}
                style={optionStyle}
                showTick={true}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar
                        size="small"
                        src={user.avatar}
                        style={{ flexShrink: 0 }}
                    >
                        {user.name?.[0]}
                    </Avatar>
                    <div style={{ fontSize: 14 }}>{user.name}</div>
                </div>
            </Select.Option>
        );
    };

    // 用于补全单个uid的详情
    const getUserByUid = async (uid) => {
        if (!uid) return null;
        let user = userList.find(i => i.uid === uid);
        if (!user) {
            try {
                const response = await request.get('/go/sp/su/listmini_service_user/', { params: { uids: uid } });
                if (response.errno === 0 && response.list && response.list.length > 0) {
                    setUserList(prev => {
                        const exists = prev.some(i => i.uid === uid);
                        return exists ? prev : [...prev, ...response.list];
                    });
                    return response.list[0];
                } else {
                    setNotFoundUids(prev => Array.from(new Set([...prev, uid])));
                }
            } catch (e) {
                setNotFoundUids(prev => Array.from(new Set([...prev, uid])));
                console.log('[UserSelector] getUserByUid error', e);
            }
        }
        return user;
    };

    if (derivedLoading) {
        return <Select loading style={{ width: '100%' }} placeholder="" multiple={multiple} />;
    }

    return (
        <Select
            loading={loading}
            value={internalValue}
            onChange={handleChange}
            style={{ width: '100%' }}
            placeholder={placeholder || (multiple ? "请选择用户（多个用逗号分隔）" : "请选择用户")}
            filter
            searchPosition='dropdown'
            showClear
            multiple={multiple}
            maxTagCount={multiple ? 3 : 1}
            filterOption={(input, option) => {
                const name = option?.label?.toString() || '';
                return name.toLowerCase().includes(input.toLowerCase());
            }}
            prefix={<IconUser style={{ color: 'var(--semi-color-primary)' }} />}
            disabled={false && disabled}
            onDropdownVisibleChange={(visible) => {
                if (visible) {
                    setSearchKey('');
                    setPagination(prev => ({ ...prev, page: 0 }));
                    // 直接传递当前选中的用户ID
                    fetchUserList(internalValue);
                }
            }}
            onSearch={(value) => {
                setSearchKey(value);
                setPagination(prev => ({ ...prev, page: 0 }));
            }}
            outerBottomSlot={
                pagination.total > (pagination.page + 1) * pagination.limit && (
                    <Button
                        type="tertiary"
                        onClick={() => {
                            setPagination(prev => ({ ...prev, page: prev.page + 1 }));
                            // 直接传递当前选中的用户ID
                            fetchUserList(internalValue);
                        }}
                        style={{ width: '100%', marginTop: '8px' }}
                    >
                        加载更多
                    </Button>
                )
            }
            outerTopSlot={
                <Button
                    icon={<IconRefresh />}
                    type="tertiary"
                    onClick={() => {
                        // 重置页码并刷新列表
                        setPagination(prev => ({ ...prev, page: 0 }));
                        // 直接传递当前选中的用户ID
                        fetchUserList(internalValue);
                    }}
                    style={{ width: '100%', marginBottom: '8px' }}
                >
                    刷新列表
                </Button>
            }
            renderSelectedItem={(option, { value } = {}) => {
                let realValue = value;
                if (realValue === undefined && option && option.value !== undefined) {
                    realValue = option.value;
                }
                if (!realValue && realValue !== 0) return '-';
                const strValue = String(realValue);
                const user = userList.find(i => String(i.uid) === strValue);
                if (user) {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Avatar size="small" src={user.avatar} style={{ flexShrink: 0 }}>{user.name?.[0]}</Avatar>
                            <span>{user.name}</span>
                        </div>
                    );
                } else if (notFoundUids.includes(realValue)) {
                    return <span style={{ color: '#aaa' }}>用户不存在</span>;
                } else {
                    if (!requestedUidsRef.current.has(strValue)) {
                        requestedUidsRef.current.add(strValue);
                        getUserByUid(realValue);
                    }
                    return '';
                }
            }}
        >
            {userList.map(user => renderCustomOption(user))}
        </Select>
    );
};

export default UserSelector;